import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

/**
 * 🔒 AuthContext — HttpOnly-cookie-only session (Feb 07, 2026 security fix).
 *
 * Previously this file persisted the JWT to localStorage and attached it via
 * `Authorization: Bearer …` on every request. That defeats the httpOnly
 * cookie the backend already sets, because any XSS could exfiltrate the
 * token from JS-readable storage.
 *
 * The backend (auth_routes.py) already sets an httpOnly + Secure + SameSite
 * cookie on /api/auth/login and /api/auth/logout clears it. The FastAPI
 * dependency `get_current_user` (core/auth.py) reads the cookie first.
 * With `axios.defaults.withCredentials = true` (set in index.js) every
 * request carries the cookie automatically — no manual header handling.
 */
const AuthContext = createContext(null);

const API = process.env.REACT_APP_BACKEND_URL;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      // Cookie auto-attached by axios.defaults.withCredentials
      const response = await axios.get(`${API}/api/auth/me`);
      setUser(response.data);
    } catch (_error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/api/auth/login`,
      { email: email.trim(), password: password.trim() }
    );
    // Backend sets httpOnly cookie in the same response — no JS token storage.
    setUser(response.data);
    return response.data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/api/auth/logout`, {});
    } catch (error) {
      console.error('Logout error:', error);
    }
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
